import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Sparkles, Loader2, Copy, Check, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'
import { buildApiUrl } from '../config/api'

interface AIAssistantProps {
  type: 'rfp' | 'response'
  onContentGenerated: (content: string) => void
  initialData?: any
}

export function AIAssistant({ type, onContentGenerated, initialData }: AIAssistantProps) {
  const [isGenerating, setIsGenerating] = useState(false)
  const [isImproving, setIsImproving] = useState(false)
  const [generatedContent, setGeneratedContent] = useState('')
  const [feedback, setFeedback] = useState('')
  const [copied, setCopied] = useState(false)

  const generateContent = async () => {
    setIsGenerating(true)
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Authentication required')
        return
      }

      const endpoint = type === 'rfp' ? '/api/ai/generate-rfp' : '/api/ai/generate-response'
      
      const response = await fetch(buildApiUrl(endpoint), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(initialData),
      })

      const data = await response.json()

      if (data.success) {
        setGeneratedContent(data.data.content)
        onContentGenerated(data.data.content)
        toast.success(`${type === 'rfp' ? 'RFP' : 'Response'} generated successfully!`)
      } else {
        toast.error(data.error || 'Failed to generate content')
      }
    } catch (error) {
      console.error('Error generating content:', error)
      toast.error('Failed to generate content')
    } finally {
      setIsGenerating(false)
    }
  }

  const improveContent = async () => {
    if (!generatedContent || !feedback.trim()) {
      toast.error('Please provide both content and feedback')
      return
    }

    setIsImproving(true)
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Authentication required')
        return
      }

      const endpoint = type === 'rfp' ? '/api/ai/improve-rfp' : '/api/ai/improve-response'
      
      const response = await fetch(buildApiUrl(endpoint), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          content: generatedContent,
          feedback: feedback,
        }),
      })

      const data = await response.json()

      if (data.success) {
        setGeneratedContent(data.data.content)
        onContentGenerated(data.data.content)
        setFeedback('')
        toast.success('Content improved successfully!')
      } else {
        toast.error(data.error || 'Failed to improve content')
      }
    } catch (error) {
      console.error('Error improving content:', error)
      toast.error('Failed to improve content')
    } finally {
      setIsImproving(false)
    }
  }

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedContent)
      setCopied(true)
      toast.success('Content copied to clipboard!')
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      toast.error('Failed to copy content')
    }
  }

  const regenerateContent = () => {
    setGeneratedContent('')
    setFeedback('')
    generateContent()
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Sparkles className="h-5 w-5 text-purple-600" />
          <span>AI Assistant</span>
        </CardTitle>
        <CardDescription>
          {type === 'rfp' 
            ? 'Generate professional RFP content with AI assistance'
            : 'Generate compelling response content with AI assistance'
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!generatedContent ? (
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-medium text-purple-900 mb-2">
                {type === 'rfp' ? 'Generate RFP Content' : 'Generate Response Content'}
              </h4>
              <p className="text-sm text-purple-700 mb-3">
                {type === 'rfp' 
                  ? 'Our AI will create a comprehensive, professional RFP document based on your requirements.'
                  : 'Our AI will create a compelling, professional response that showcases your company\'s capabilities.'
                }
              </p>
              <Button 
                onClick={generateContent}
                disabled={isGenerating}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generate with AI
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Generated Content</h4>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyToClipboard}
                  disabled={copied}
                >
                  {copied ? (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="mr-2 h-4 w-4" />
                      Copy
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={regenerateContent}
                  disabled={isGenerating}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Regenerate
                </Button>
              </div>
            </div>
            
            <div className="bg-gray-50 border rounded-lg p-4 max-h-96 overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm font-mono">
                {generatedContent}
              </pre>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium">
                Improve Content (Optional)
              </label>
              <Textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Provide feedback on what you'd like to improve..."
                rows={3}
              />
              <Button
                onClick={improveContent}
                disabled={isImproving || !feedback.trim()}
                variant="outline"
                className="w-full"
              >
                {isImproving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Improving...
                  </>
                ) : (
                  'Improve Content'
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
